/**
 * Helper functions for test data generation.
 */
import type { NftInfo } from '@phalaworld/types/rmrk'

import type { ApiPromise } from '@polkadot/api'
import type { StorageKey } from '@polkadot/types';
import type { AnyJson, Codec } from '@polkadot/types/types';
import type { Permill } from '@polkadot/types/interfaces';
import type { KeyringPair } from '@polkadot/keyring/types';

import { Keyring } from '@polkadot/keyring'
import { mnemonicGenerate } from '@polkadot/util-crypto'
import { BN_MILLION } from '@polkadot/util'
import * as R from 'ramda'
import { pipe } from 'fp-ts/lib/function'
import * as TE from 'fp-ts/lib/TaskEither'
import * as E from 'fp-ts/lib/Either'
import BN from 'bn.js'

// @FIXME using relation import here cause the tsc result is not correct
import { RACE_CHAIN_MAP, CAREER_CHAIN_MAP } from '../types/nftAttribute'

import { shuffle } from './shuffle'
import { waitExtrinsicFinished } from './waitExtrinsicFinished'
import { getEventData } from './SubmittableResultUtils'

/**
  * Create a new BN instance with the number of PHA.
  */
export function pha(n: number) {
  return new BN(n).mul(new BN(1e12))
}

export function transformEntries<TValue=AnyJson, TKey=string[]>(entries: [StorageKey, Codec][]): [TKey[], TValue][] {
  return entries.map(([key, value]) => {
    const keys = key.args.map(i => i.toPrimitive())
    return [keys, value.toJSON()] as [TKey[], TValue]
  })
}

export function makeFakeNft({ accountId, nftId = 0 }: { accountId: string, nftId?: number }): NftInfo {
  return {
    collectionId: 1,
    nftId,
    thumbnailUrl: 'https://example.com/nft-thumbnail.png',
    styleToken: '0x1234...',
    owner: {
      accountId,
    },
    metadataUrl: 'https://example.com/nft-metadata.json',
    metadata: {
      name: 'Example NFT',
      description: 'This is an example NFT.',
      image: 'https://example.com/nft-image.png',
    },
    royalty: {
      amount: new BN(10),
      recipient: '0x9876...',
    },
    pending: false,
    transferable: true,
  }
}

export function permillToNumber(permill: Permill): number {
  return permill.mul(new BN(1_00_00)).div(BN_MILLION).toNumber() / 100_00
}

export function numberToPermill(n: number): BN {
  return new BN(n * 100_00).mul(BN_MILLION).div(new BN(1_00_00))
}

export interface NestedNftTreeRelation extends Record<number, number[]> {
  __root__?: number[]
}

export interface NestedNftTree {
  nodes: number[]
  relations: NestedNftTreeRelation
}

export async function findChildren(api: ApiPromise, collectionId: number, nftId: number, isRoot = false): Promise<NestedNftTree> {
  const children = await api.query.rmrkCore.children.entries([collectionId, nftId])
  const transformed = transformEntries(children)
  const pairs = R.map(R.pipe(R.pathOr<string[]>([], [0, 1]), R.map(Number)), transformed)
  let nodes: number[] = R.map(R.nth(1), pairs) as number[]
  let relations: NestedNftTreeRelation = {}
  if (isRoot) {
    relations.__root__ = R.clone(nodes)
  }
  if (pairs.length > 0) {
    const batch = await Promise.all(R.map(([_collectionId, _nftId]) => findChildren(api, _collectionId, _nftId), pairs))
    for (let [_batch, _pair] of R.zip(batch, pairs)) {
      if (_batch.nodes.length === 0) {
        continue
      }
      nodes = R.concat(nodes, _batch.nodes)
      relations = R.mergeRight(relations, _batch.relations)
      relations[_pair[1]] = _batch.nodes
    }
  }
  return { nodes, relations }
}

export async function buildNestedNftTree(api: ApiPromise, collectionId: number, nftId: number): Promise<NestedNftTree> {
  return await findChildren(api, collectionId, nftId, true)
}

const shuffleRace = R.pipe(
  () => R.values(RACE_CHAIN_MAP),
  shuffle,
)

const shuffleCareer = R.pipe(
  () => R.values(CAREER_CHAIN_MAP),
  shuffle,
)

// @FIXME maybe create a faker for this
const jacketChosenPart = {
    'parts': {
        "jacket": {
            'shell_part': {
                'name': "jacket",
                'rarity': 'Epic',
                'metadata': null,
                'layer': 0,
                'x': 0,
                'y': 0,
                'tradeable': true,
            },
            'sub_parts': [
                {
                    'name': "jacket-details",
                    'rarity': 'Legend',
                    'metadata': "ar://jacket-details-uri",
                    'layer': 0,
                    'x': 0,
                    'y': 0,
                    'tradeable': false,
                },
                {
                    'name': "jacket-hat",
                    'rarity': 'Epic',
                    'metadata': "ar://jacket-hat-uri",
                    'layer': 0,
                    'x': 0,
                    'y': 0,
                    'tradeable': false,
                },
                {
                    'name': "jacket",
                    'rarity': 'Rare',
                    'metadata': "ar://jacket-uri",
                    'layer': 0,
                    'x': 0,
                    'y': 0,
                    'tradeable': false,
                }
            ],
        },
        't_shirt': {
            'shell_part': {
                'name': 't_shirt',
                'rarity': 'Rare',
                'metadata': "ar://t-shirt-uri",
                'layer': 0,
                'x': 0,
                'y': 0,
                'tradeable': true,
            },
            'sub_parts': null,
        },
        'shoes': {
            'shell_part': {
                'name': "shoes",
                'rarity': 'Rare',
                'metadata': null,
                'layer': 0,
                'x': 0,
                'y': 0,
                'tradeable': true,
            },
            'sub_parts': [
                {
                    'name': "shoes-details",
                    'rarity': 'Rare',
                    'metadata': "ar://shoes-details-uri",
                    'layer': 0,
                    'x': 0,
                    'y': 0,
                    'tradeable': true,
                },
                {
                    'name': "shoes",
                    'rarity': 'Rare',
                    'metadata': "ar://shoes-uri",
                    'layer': 0,
                    'x': 0,
                    'y': 0,
                    'tradeable': false,
                }
            ],
        }
    }
};


const keyring = new Keyring({ type: 'sr25519' })

export const makeKeyringPair = () => {
  const mnemonic = mnemonicGenerate()
  const pair = keyring.createFromUri(mnemonic)
  const address = keyring.encodeAddress(pair.address, 30)
  return { pair, address }
}

export async function transferPha(api: ApiPromise, from: KeyringPair, to: KeyringPair, amount: number) {
  await waitExtrinsicFinished(api, api.tx.balances.transfer(to.address, pha(amount)), from)
}

export async function claimSpirit(api: ApiPromise, account: KeyringPair) {
  const result = await waitExtrinsicFinished(api, api.tx.pwNftSale.claimSpirit(), account)
  const spiritId = R.nth<number>(1, getEventData(result, 'SpiritClaimed'))!
  return spiritId
}

export async function buyPrimeOriginShell(api: ApiPromise, overlord: KeyringPair, account: KeyringPair) {
  // Pre-order Origin Shell.
  const result1 = await waitExtrinsicFinished(api, api.tx.pwNftSale.preorderOriginOfShell(shuffleRace(), shuffleCareer()), account)
  const preOrderId = R.nth<number>(1, getEventData(result1, 'OriginOfShellPreordered'))!

  // Mint Origin Shell.
  const result2 = await waitExtrinsicFinished(api, api.tx.pwNftSale.mintChosenPreorders([preOrderId]), overlord)
  const originShellId = R.nth<number>(2, getEventData(result2, 'NftMinted'))!
  return { preOrderId, originShellId }
}

export async function hatchOriginShell(api: ApiPromise, overlord: KeyringPair, account: KeyringPair, originShellId: number) {
  await waitExtrinsicFinished(api, api.tx.pwIncubation.startIncubation(1, originShellId), account)
  const batchResult = await waitExtrinsicFinished(api, api.tx.utility.batch([
    api.tx.pwIncubation.setOriginOfShellChosenParts(1, originShellId, jacketChosenPart),
    api.tx.pwIncubation.hatchOriginOfShell(1, originShellId, ""),
  ]), overlord)
  const shellId = R.nth<number>(2, getEventData(batchResult, 'NftMinted'))!
  return shellId
}

export async function makeAccountClaimedSpirit(api: ApiPromise, treasury: KeyringPair, balances: number = 11) {
  const { pair, address } = makeKeyringPair()
  const result = await pipe(
    TE.tryCatch(() => transferPha(api, treasury, pair, balances), E.toError),
    TE.chain(() => TE.tryCatch(() => claimSpirit(api, pair), E.toError)),
    TE.map(spiritId => ({ pair, address, spiritId })),
  )()
  if (E.isRight(result)) {
    return result.right
  }
  throw new Error(`Failed to make account claimed spirit: [${address}] ${result.left}`)
}

export async function makeAccountOwnedOriginShellNft(api: ApiPromise, treasury: KeyringPair, overlord: KeyringPair) {
  const { pair, address } = makeKeyringPair()
  const result = await pipe(
    TE.tryCatch(() => transferPha(api, treasury, pair, 501), E.toError),
    TE.chain(() => TE.tryCatch(async () => {
      const batchResult = await waitExtrinsicFinished(api, api.tx.utility.batch([
        api.tx.pwNftSale.claimSpirit(),
        api.tx.pwNftSale.preorderOriginOfShell(shuffleRace(), shuffleCareer()),
      ]), pair)
      const spiritId = R.nth<number>(1, getEventData(batchResult, 'SpiritClaimed'))!
      const preOrderId = R.nth<number>(1, getEventData(batchResult, 'OriginOfShellPreordered'))!
      const mintedResult = await waitExtrinsicFinished(api, api.tx.pwNftSale.mintChosenPreorders([preOrderId]), overlord)
      const originShellId = R.nth<number>(2, getEventData(mintedResult, 'NftMinted'))!
      return { spiritId, preOrderId, originShellId }
    }, E.toError)),
    TE.map(({ originShellId }) => ({ pair, address, originShellId })),
  )()
  if (E.isRight(result)) {
    return result.right
  }
  throw new Error(`Failed to make account owned origin shell NFT: [${address}] ${result.left}`)
}

export async function makeAccountOwnedShellNft(api: ApiPromise, treasury: KeyringPair, overlord: KeyringPair) {
  const { pair, address } = makeKeyringPair()
  const result = await pipe(
    TE.tryCatch(() => transferPha(api, treasury, pair, 501), E.toError),
    TE.chain(() => TE.tryCatch(
      async () => {
        const batchResult = await waitExtrinsicFinished(api, api.tx.utility.batch([
          api.tx.pwNftSale.claimSpirit(),
          api.tx.pwNftSale.preorderOriginOfShell(shuffleRace(), shuffleCareer()),
        ]), pair)
        const preOrderId = R.nth<number>(1, getEventData(batchResult, 'OriginOfShellPreordered'))!
        const mintResult = await waitExtrinsicFinished(api, api.tx.pwNftSale.mintChosenPreorders([preOrderId]), overlord)
        const originShellId = R.nth<number>(2, getEventData(mintResult, 'NftMinted'))!
        return { preOrderId, originShellId }
      },
      E.toError
    )),
    TE.chain(({ originShellId }) => TE.tryCatch(() => hatchOriginShell(api, overlord, pair, originShellId), E.toError)),
    TE.map(shellId => ({ pair, address, shellId })),
  )()
  if (E.isRight(result)) {
    return result.right
  }
  throw new Error(`Failed to make account owned shell NFT: [${address}] ${result.left.message}`)
}

